import interceptor from '../common/interceptor';
import { APIPaths } from '../common/constants';
import { ApiResponse, PaginatedApiResponse } from '@/apis/types';
import { OperationType } from '../common/enum';

export type CredentialAuthType = 'API_KEY' | 'OAUTH_CLIENT_CREDENTIALS';
export type CredentialStatus = 'PENDING' | 'SYNCED' | 'ERROR';
export type ApiKeyPlacement = 'HEADER' | 'QUERY';

/** One credential as the list endpoint returns it; never includes the secret. Mirrors `CredentialListItemSerializer` in
 * customer_credentials/serializer/credential_serializer.py. */
export interface CredentialData {
  id: string;
  name: string;
  auth_type: CredentialAuthType;
  details: string;
  // Non-secret config, for pre-filling the Edit form. The secret itself is
  // never returned by any endpoint.
  api_key_placement?: ApiKeyPlacement | null;
  api_key_param_name?: string | null;
  oauth_token_url?: string | null;
  oauth_client_id?: string | null;
  oauth_scopes?: string;
  status: CredentialStatus;
  // Why a Failed credential failed; shown on hover. Secrets are redacted
  // server-side before this is stored.
  status_detail?: string;
  last_used_at: string | null;
  created_by_name: string | null;
  created_at: string;
}

/** Body of the edit request; every field is optional and an omitted one is left untouched. `secret` is the write-only
 * "Replace" value, and type and API-key placement can't be changed. Mirrors `UpdateCredentialSerializer`. */
export interface UpdateCredentialPayload {
  name?: string;
  api_key_param_name?: string;
  oauth_token_url?: string;
  oauth_client_id?: string;
  oauth_scopes?: string;
  secret?: string;
}

/** Query options for one page of the Settings list; `pageSize` defaults to 50. */
export interface CredentialListParams {
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface CreateCredentialPayload {
  name: string;
  auth_type: CredentialAuthType;
  secret: string;
  api_key_placement?: ApiKeyPlacement;
  api_key_param_name?: string;
  oauth_token_url?: string;
  oauth_client_id?: string;
  oauth_scopes?: string;
}

// GET /api/customer-credentials/ -- never returns secret material, see
// customer_credentials/serializer/credential_serializer.py on the backend.
export const GetCustomerCredentials = (
  token: string,
  authType?: CredentialAuthType
): Promise<ApiResponse<CredentialData[]>> => {
  const params = new URLSearchParams();
  if (authType) params.append('auth_type', authType);
  const query = params.toString();

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.customerCredentials}${query ? `?${query}` : ''}`,
        token,
        null
      )
      .then((res) => resolve(res as unknown as ApiResponse<CredentialData[]>))
      .catch((error) => reject(error?.response));
  });
};

/** The paginated envelope of the Settings list, with the counts the pager needs. */
export interface CredentialListPage {
  data?: CredentialData[];
  is_success: boolean;
  message?: string;
  page_count?: number;
  total_count?: number;
}

/** Returns one searchable page of credentials for the Settings table. A separate call from `GetCustomerCredentials`,
 * whose flat list the node panel depends on. */
export const GetCustomerCredentialsPage = (
  token: string,
  { search, page = 1, pageSize = 50 }: CredentialListParams = {}
): Promise<{ data: { data: CredentialListPage } }> => {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  if (search) params.append('search', search);

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.customerCredentials}?${params.toString()}`,
        token,
        null
      )
      .then((res) =>
        resolve(res as unknown as PaginatedApiResponse<CredentialData[]>)
      )
      .catch((error) => reject(error?.response));
  });
};

/** Edits a credential. Failures reject without the interceptor's toast, because the caller shows them (a taken name
 * appears under the Name field). */
export const UpdateCustomerCredential = (
  token: string,
  id: string,
  model: UpdateCredentialPayload
): Promise<ApiResponse<CredentialData>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Patch',
        `${APIPaths.customerCredentials}${id}/`,
        model,
        token,
        undefined,
        OperationType.Write,
        true
      )
      .then((res) => resolve(res as unknown as ApiResponse<CredentialData>))
      .catch((error) => reject(error?.response));
  });
};

/** Creates a credential. Failures reject without the interceptor's toast, because the caller shows them. */
export const CreateCustomerCredential = (
  token: string,
  model: CreateCredentialPayload
): Promise<ApiResponse<CredentialData>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        APIPaths.customerCredentials,
        model,
        token,
        undefined,
        OperationType.Write,
        true
      )
      .then((res) => resolve(res as unknown as ApiResponse<CredentialData>))
      .catch((error) => reject(error?.response));
  });
};

/** Replaces a credential's secret. Deliberately separate from a generic update: rotating is always an explicit,
 * auditable action (see customer_credentials/views/credentials_view.py). Failures reject without the interceptor's
 * toast, because the caller shows them. */
export const RotateCustomerCredentialSecret = (
  token: string,
  id: string,
  secret: string
): Promise<ApiResponse<CredentialData>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${APIPaths.customerCredentials}${id}/rotate/`,
        { secret },
        token,
        undefined,
        OperationType.Write,
        true
      )
      .then((res) => resolve(res as unknown as ApiResponse<CredentialData>))
      .catch((error) => reject(error?.response));
  });
};

/** Archives the credential (soft delete). Allowed while automations still use it, so a credential can be revoked at
 * once in a security incident: referencing nodes then show "Credential missing" and fail at their next run. */
export const DeleteCustomerCredential = (
  token: string,
  id: string
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete(
        'Delete',
        `${APIPaths.customerCredentials}${id}/`,
        null,
        token
      )
      .then((res) => resolve(res as unknown as ApiResponse))
      .catch((error) => reject(error?.response));
  });
};
